/**
 * Centralized error handling utility
 * Provides consistent error messages and user-friendly error display
 */

import logger from './logger';

/**
 * Error types and their user-friendly messages
 */
const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Cannot connect to the server. Please check your internet connection.',
  TIMEOUT: 'Request timed out. The server is taking too long to respond.',
  CONNECTION_REFUSED: 'Cannot connect to backend server. Please ensure the backend is running.',
  
  // HTTP errors
  400: 'Invalid request. Please check your input and try again.',
  401: 'You are not authenticated. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This resource already exists.',
  422: 'Invalid data provided. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Server error. Please try again later or contact support.',
  502: 'Bad gateway. The server is temporarily unavailable.',
  503: 'Service unavailable. The server is not ready. Please try again in a moment.',
  504: 'Gateway timeout. The server is taking too long to respond.',
  
  // Application-specific errors
  INTERNAL_ERROR: 'Server error. Please try again later or contact support.',
  VALIDATION_FAILED: 'Invalid data provided. Please check your input.',
  VISUALIZER_TIMEOUT: 'Visualization timed out. Try simplifying your query or reducing the data range.',
  VISUALIZER_UNAVAILABLE: 'Visualization service is unavailable. Please try again later.',
  VISUALIZER_ERROR: 'Error generating visualization. Please check your query and try again.',
  SQL_INVALID: 'Invalid SQL query. Please check your syntax.',
  RATE_LIMIT_EXCEEDED: 'Rate limit exceeded. Please wait before making more requests.',
  SUBSCRIPTION_REQUIRED: 'This feature requires a higher subscription level.',
  PREMIUM_REQUIRED: 'This feature is only available for premium users.',
};

/**
 * Get user-friendly error message from error object
 */
export const getErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred';
  
  // If it's already a user-friendly string, return it
  if (typeof error === 'string') {
    return error;
  }
  
  // Check for error response from API
  if (error.response?.data) {
    const data = error.response.data;
    
    // Prefer user-friendly message from backend
    if (data.message) {
      return data.message;
    }

    // Per-field validation errors (DRF-style: {"email": ["Email already registered"]})
    // are more specific than the generic "Validation failed" in `data.error` — prefer them.
    if (data.errors && typeof data.errors === 'object') {
      const firstFieldError = Object.values(data.errors)[0];
      const firstMessage = Array.isArray(firstFieldError) ? firstFieldError[0] : firstFieldError;
      if (firstMessage) {
        return firstMessage;
      }
    }

    // Check error code
    if (data.error_code && ERROR_MESSAGES[data.error_code]) {
      return ERROR_MESSAGES[data.error_code];
    }

    // Check status code
    if (data.status_code && ERROR_MESSAGES[data.status_code]) {
      return ERROR_MESSAGES[data.status_code];
    }

    // Use error field
    if (data.error) {
      return data.error;
    }
  }
  
  // Check HTTP status code
  if (error.response?.status && ERROR_MESSAGES[error.response.status]) {
    return ERROR_MESSAGES[error.response.status];
  }
  
  // Check error code
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check error message
  if (error.message) {
    // Map common error messages
    if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT;
    }
    if (error.message.includes('ECONNREFUSED') || error.message.includes('Connection refused')) {
      return ERROR_MESSAGES.CONNECTION_REFUSED;
    }
    
    // Return message if it seems user-friendly
    if (error.message.length < 200 && !error.message.includes('Error:') && !error.message.includes('at ')) {
      return error.message;
    }
  }
  
  // Default message
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get error code from error object
 */
export const getErrorCode = (error) => {
  if (!error) return 'UNKNOWN';
  
  if (error.response?.data?.error_code) {
    return error.response.data.error_code;
  }
  
  if (error.response?.data?.code) {
    return error.response.data.code;
  }
  
  if (error.response?.status) {
    return `HTTP_${error.response.status}`;
  }
  
  if (error.code) {
    return error.code;
  }
  
  return 'UNKNOWN';
};

/**
 * Check if error is retryable
 */
export const isRetryable = (error) => {
  if (!error) return false;
  
  // Network errors are usually retryable
  if (!error.response) {
    return true;
  }
  
  // 5xx errors are retryable (except 501)
  const status = error.response.status;
  if (status >= 500 && status !== 501) {
    return true;
  }
  
  // 429 (rate limit) is retryable after delay
  if (status === 429) {
    return true;
  }
  
  // 503 (service unavailable) is retryable
  if (status === 503) {
    return true;
  }
  
  // 504 (gateway timeout) is retryable
  if (status === 504) {
    return true;
  }
  
  return false;
};

/**
 * Get retry delay in milliseconds
 */
export const getRetryDelay = (error, attempt = 0) => {
  // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
  const baseDelay = 1000;
  const maxDelay = 30000;
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 1000;
  
  return delay + jitter;
};

/**
 * Handle error with retry logic
 */
export const handleErrorWithRetry = async (fn, maxRetries = 3, onRetry = null) => {
  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if error is not retryable
      if (!isRetryable(error)) {
        throw error;
      }
      
      // Don't retry if we've exhausted retries
      if (attempt >= maxRetries) {
        break;
      }
      
      // Calculate delay and wait
      const delay = getRetryDelay(error, attempt);
      
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, delay);
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Log error appropriately based on severity
 */
export const logError = (error, context = {}) => {
  // Don't log expected errors (like 401 on /auth/me when not logged in)
  if (error?.isExpected || error?.silent) {
    return;
  }
  
  const errorCode = getErrorCode(error);
  const errorMessage = getErrorMessage(error);
  
  // Log based on severity
  if (error.response?.status >= 500) {
    logger.error('Server error:', {
      code: errorCode,
      message: errorMessage,
      status: error.response?.status,
      ...context
    });
  } else if (error.response?.status >= 400) {
    logger.warn('Client error:', {
      code: errorCode,
      message: errorMessage,
      status: error.response?.status,
      ...context
    });
  } else {
    logger.error('Network error:', {
      code: errorCode,
      message: errorMessage,
      ...context
    });
  }
};

/**
 * Show error to user (can be extended to use toast notifications)
 */
export const showError = (error, options = {}) => {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  
  // Log error
  if (!options.silent) {
    logError(error, options.context);
  }
  
  // Return error object for component to display
  return {
    message,
    code,
    retryable: isRetryable(error),
    originalError: error
  };
};

/**
 * Format error for display in UI
 */
export const formatErrorForDisplay = (error) => {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  
  return {
    title: getErrorTitle(code),
    message,
    code,
    retryable: isRetryable(error),
    suggestions: getErrorSuggestions(code, error)
  };
};

/**
 * Get error title based on code
 */
const getErrorTitle = (code) => {
  const titles = {
    NETWORK_ERROR: 'Connection Error',
    TIMEOUT: 'Request Timeout',
    CONNECTION_REFUSED: 'Server Unavailable',
    VISUALIZER_TIMEOUT: 'Visualization Timeout',
    VISUALIZER_UNAVAILABLE: 'Service Unavailable',
    HTTP_401: 'Authentication Required',
    HTTP_403: 'Access Denied',
    HTTP_404: 'Not Found',
    HTTP_429: 'Too Many Requests',
    HTTP_500: 'Server Error',
    HTTP_503: 'Service Unavailable',
    HTTP_504: 'Gateway Timeout',
  };
  
  return titles[code] || 'Error';
};

/**
 * Get suggestions for resolving error
 */
const getErrorSuggestions = (code, error) => {
  const suggestions = [];
  
  switch (code) {
    case 'NETWORK_ERROR':
    case 'CONNECTION_REFUSED':
      suggestions.push('Check your internet connection');
      suggestions.push('Verify the backend server is running');
      suggestions.push('Check firewall settings');
      break;
      
    case 'TIMEOUT':
    case 'HTTP_504':
      suggestions.push('The server is taking longer than expected');
      suggestions.push('Try again in a few moments');
      suggestions.push('If the problem persists, contact support');
      break;
      
    case 'HTTP_503':
      suggestions.push('The service is temporarily unavailable');
      suggestions.push('Please wait a moment and try again');
      break;
      
    case 'HTTP_429':
      suggestions.push('You have made too many requests');
      suggestions.push('Please wait a moment before trying again');
      break;
      
    case 'HTTP_401':
      suggestions.push('Your session may have expired');
      suggestions.push('Please log in again');
      break;
      
    case 'HTTP_403':
      suggestions.push('You may need a higher subscription level');
      suggestions.push('Contact support if you believe this is an error');
      break;
      
    case 'VISUALIZER_TIMEOUT':
      suggestions.push('Try simplifying your query');
      suggestions.push('Reduce the date range or number of variables');
      suggestions.push('Check if your query is too complex');
      break;
      
    case 'VISUALIZER_UNAVAILABLE':
      suggestions.push('The visualization service is temporarily unavailable');
      suggestions.push('Please try again in a few moments');
      break;
      
    default:
      if (error.response?.status >= 500) {
        suggestions.push('This appears to be a server error');
        suggestions.push('Please try again later');
        suggestions.push('If the problem persists, contact support');
      }
  }
  
  return suggestions;
};

const errorHandler = {
  getErrorMessage,
  getErrorCode,
  isRetryable,
  getRetryDelay,
  handleErrorWithRetry,
  logError,
  showError,
  formatErrorForDisplay
};

export default errorHandler;

